import {
  forwardRef,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import { useTranslation } from "react-i18next";
import { cn } from "src/shadcn/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/primitives/Dialog";
import { Button } from "src/components/Button";

import { SIZE_CLASS_MAP, STYLE_VARIANT_MAP } from "./constants";

export type AlertStyle = "danger" | "warning";
export type AlertSize = "small" | "medium" | "large";
type InitialFocusElement = "submit" | "cancel";

/** Radix Dialog.Content props we forward via ...otherProps. */
type DialogContentProps = React.ComponentProps<typeof DialogContent>;

export interface AlertProps extends Omit<
  DialogContentProps,
  | "children"
  | "role"
  | "showCloseButton"
  | "className"
  | "style"
  | "onEscapeKeyDown"
  | "onInteractOutside"
> {
  /** Size of the alert dialog. */
  size?: AlertSize;
  /** Whether the alert is open. */
  isOpen?: boolean;
  /** Whether the submit action is in progress. */
  isSubmitting?: boolean;
  /** Additional CSS class names applied to the dialog content. */
  className?: string;
  /** Close on pressing the Esc key. */
  closeOnEsc?: boolean;
  /** Show the close button. */
  closeButton?: boolean;
  /** Additional CSS class names applied to the backdrop/overlay. */
  backdropClassName?: string;
  /** Close on clicking outside the dialog. */
  closeOnOutsideClick?: boolean;
  /** Callback invoked when the alert is closed. */
  onClose?: () => void;
  /** Callback invoked when the submit button is clicked. */
  onSubmit?: () => void;
  /** Title text of the alert. */
  title?: string;
  /** Message content of the alert. */
  message?: ReactNode;
  /** Label for the submit button. */
  submitButtonLabel?: string;
  /** Label for the cancel button. */
  cancelButtonLabel?: string;
  /** Hide the cancel button. */
  hideCancelButton?: boolean;
  /** Visual style of the submit button. */
  style?: AlertStyle;
  /** Ref of the element to receive focus when the alert opens. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Which built-in button receives focus on open: "submit" or "cancel". */
  initialFocusElement?: InitialFocusElement;
  /** Radix Dialog `modal` prop — controls modal vs non-modal behaviour. */
  modal?: boolean;
  /** Radix Dialog `defaultOpen` prop. */
  defaultOpen?: boolean;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      size = "medium",
      isOpen = false,
      isSubmitting = false,
      className,
      closeOnEsc = true,
      closeButton = true,
      backdropClassName,
      closeOnOutsideClick = true,
      onClose = () => {},
      onSubmit = () => {},
      title = "",
      message = "",
      submitButtonLabel: submitButtonLabelProp,
      cancelButtonLabel: cancelButtonLabelProp,
      hideCancelButton = false,
      style = "danger",
      initialFocusRef,
      initialFocusElement,
      modal,
      defaultOpen,
      ...otherProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const submitButtonLabel =
      submitButtonLabelProp ?? t("neetoatoms.alert.continue", "Continue");
    const cancelButtonLabel =
      cancelButtonLabelProp ?? t("neetoatoms.alert.cancel", "Cancel");
    const baseId = useId();
    const titleId = `alert-title-${baseId}`;
    const messageId = `alert-message-${baseId}`;
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // Base UI consolidates the per-interaction dismiss callbacks into
    // onOpenChange's eventDetails, and takes initial focus as a ref.
    const handleOpenChange: React.ComponentProps<
      typeof Dialog
    >["onOpenChange"] = (open, eventDetails) => {
      if (open) return;
      if (
        (eventDetails.reason === "escape-key" && !closeOnEsc) ||
        (eventDetails.reason === "outside-press" && !closeOnOutsideClick)
      ) {
        eventDetails.cancel();

        return;
      }
      onClose();
    };

    // Custom focus ref takes highest priority, then the named button
    const initialFocus =
      initialFocusRef ??
      (initialFocusElement === "submit"
        ? submitButtonRef
        : initialFocusElement === "cancel"
          ? cancelButtonRef
          : undefined);

    return (
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
        defaultOpen={defaultOpen}
      >
        <DialogContent
          ref={ref}
          role="alertdialog"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={message ? messageId : undefined}
          showCloseButton={closeButton}
          overlayClassName={backdropClassName}
          initialFocus={initialFocus ?? undefined}
          className={cn(SIZE_CLASS_MAP[size], className)}
          data-testid="alert-box"
          {...otherProps}
        >
          <DialogHeader>
            <DialogTitle
              id={titleId}
              data-testid="alert-title"
              className="text-2xl font-semibold"
            >
              {title}
            </DialogTitle>
            {message && (
              <DialogDescription id={messageId} data-testid="alert-message">
                {message}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            {!hideCancelButton && (
              <Button
                ref={cancelButtonRef}
                variant="outline"
                label={cancelButtonLabel}
                onClick={onClose}
                data-testid="alert-cancel-button"
              />
            )}
            <Button
              ref={submitButtonRef}
              variant={STYLE_VARIANT_MAP[style]}
              label={submitButtonLabel}
              loading={isSubmitting}
              disabled={isSubmitting || !isOpen}
              onClick={onSubmit}
              data-testid="alert-submit-button"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

Alert.displayName = "Alert";

export { Alert };
